// StreamingTTSManager.js
/**
 * Streaming TTS Manager
 * Integrates RealtimeLLMToElevenLabs with the existing chat system
 */
import { RealtimeLLMToElevenLabs } from "./RealtimeLLMToElevenLabs.js";
import { MinimalLipSyncController } from "./LipSyncController.minimal.js";

export class StreamingTTSManager {
  constructor() {
    this.realtimeTTS = null;
    this.currentCharacterVoiceId = null;
    this.elevenLabsApiKey = null;
    this.isActive = false;
    this.modelController = null;
    this.volumeController = null;

    // Callbacks for integration with existing system
    this.onSpeechStart = null;
    this.onSpeechEnd = null;
    this.onAudioChunk = null; // (reserved for future use)
  }

  /**
   * Initialize with character-specific voice configuration
   */
  async initialize(characterId, elevenLabsApiKey) {
    console.log(`[StreamingTTS] Initializing for character: ${characterId}`);

    this.elevenLabsApiKey = elevenLabsApiKey;

    // Get voice configuration for the character
    const voiceConfig = this.getCharacterVoiceConfig(characterId);
    if (!voiceConfig) {
      console.warn(
        `[StreamingTTS] No voice config found for character: ${characterId}`,
      );
      return false;
    }

    this.currentCharacterVoiceId = voiceConfig.voice_id;

    // Initialize the realtime TTS engine (PERSISTENT WS ENABLED)
    this.realtimeTTS = new RealtimeLLMToElevenLabs({
      voiceId: this.currentCharacterVoiceId,
      elevenLabsApiKey: this.elevenLabsApiKey,
      modelId: "eleven_flash_v2_5",
      outputFormat: "pcm_16000",
      syncAlignment: true,
      // Task #99: sentence-boundary outbound segmentation (engine defaults).
      jitterMs: 160,

      // Keep the ElevenLabs WS connected until shutdown()
      persistentWs: true,
      // lipSyncOpts: { enableMouthDrive: false }
      // You can also pass lipSyncOpts here if you prefer
    });

    // Initialize lip sync if we have model controller with blend shapes
    this._initializeLipSync();

    console.log(
      `[StreamingTTS] Initialized with voice ID: ${this.currentCharacterVoiceId}`,
    );
    return true;
  }

  /**
   * Get character-specific voice configuration
   */
  getCharacterVoiceConfig(characterId) {
    const CHARACTER_VOICE_CONFIGS = {
      "bc5d31a8-047b-4f72-8f16-fea7a365ca69": {
        // Richard Feynman
        voice_id: "fBOMlsQwDZ1UMIVIHGU2",
        settings: {
          stability: 0.5,
          similarity_boost: 0.75,
          style: 0.0,
          use_speaker_boost: true,
        },
      },
      "88c314c4-5890-4e55-9418-8b46a376ef81a": {
        // Friedrich Nietzsche
        voice_id: "nP0opz5wvlz83TcFLppE",
        settings: {
          stability: 0.5,
          similarity_boost: 0.75,
          style: 0.0,
          use_speaker_boost: true,
        },
      },
      "6da2e341-3d4e-4a06-bf8c-5cb8f499b93e": {
        // Marie Curie
        voice_id: "tHTI2qB5SvRdGAzCpaQF",
        settings: {
          stability: 0.85,
          similarity_boost: 0.85,
          style: 0.0,
          use_speaker_boost: true,
        },
      },
      "802bd089-d231-4c4e-830c-476199321af8": {
        // Niels Bohr
        voice_id: "wmNy3H4pgGksDO4tuY9p",
        settings: {
          stability: 0.78,
          similarity_boost: 0.8,
          style: 0.0,
          use_speaker_boost: true,
        },
      },

      "770e2d93-a6a4-41ed-aca1-8951327885b5": {
        // Amelia Earhart
        voice_id: "9BWtsMINqrJLrRacOk9x",
        settings: {
          stability: 0.85,
          similarity_boost: 0.85,
          style: 0.0,
          use_speaker_boost: true,
        },
      },
      "3a06a73a-6a55-4bb6-a5e4-8a58aee63fa7": {
        // Salvador Dali
        voice_id: "fBOMlsQwDZ1UMIVIHGU2",
        settings: {
          stability: 0.6,
          similarity_boost: 0.75,
          style: 0.0,
          use_speaker_boost: true,
        },
      },
      "c88e35e4-a5d8-4c1e-bb97-6e7eaae3e9e7": {
        // Abraham Lincoln
        voice_id: "cgSgspJ2msm6clMCkdW9",
        settings: {
          stability: 0.9,
          similarity_boost: 0.9,
          style: 0.0,
          use_speaker_boost: true,
        },
      },
      "4f50a698-6034-49c1-b9d4-221f59bf2378": {
        // Frida Kahlo
        voice_id: "0usCDMt9MmbqzEgFEMlP",
        settings: {
          stability: 0.5,
          similarity_boost: 0.75,
          style: 0.0,
          use_speaker_boost: true,
        },
      },
      "91d5cec5-1c21-423f-bbb4-bceb0f77a6c9": {
        // Nostradamus
        voice_id: "dM5I3tskULdOgmbSBDmR",
        settings: {
          stability: 0.5,
          similarity_boost: 0.75,
          style: 0.0,
          use_speaker_boost: true,
        },
      },
    };

    return CHARACTER_VOICE_CONFIGS[characterId];
  }

  /**
   * Set controller references for integration
   */
  setControllers({ modelController, volumeController }) {
    this.modelController = modelController;
    this.volumeController = volumeController;
    console.log("[StreamingTTS] Controllers set");
  }

  /**
   * Set callbacks for speech events
   */
  setCallbacks({ onSpeechStart, onSpeechEnd, onAudioChunk }) {
    this.onSpeechStart = onSpeechStart;
    this.onSpeechEnd = onSpeechEnd;
    this.onAudioChunk = onAudioChunk;
    console.log("[StreamingTTS] Callbacks configured");
  }

  /**
   * Start a new assistant turn - called before OpenAI streaming begins
   */
  async onAssistantTurnStart() {
    if (!this.realtimeTTS) {
      console.warn(
        "[StreamingTTS] Not initialized - cannot start assistant turn",
      );
      return;
    }

    try {
      console.log("[StreamingTTS] Starting assistant turn");
      await this.realtimeTTS.start();
      this.isActive = true;

      // Trigger speaking animation
      if (this.modelController?.startSpeaking) {
        this.modelController.startSpeaking();
      }

      // Notify callbacks
      if (this.onSpeechStart) {
        this.onSpeechStart();
      }
    } catch (error) {
      console.error("[StreamingTTS] Error starting assistant turn:", error);
    }
  }

  /**
   * Process OpenAI delta tokens - called for each streaming token
   * @param {string} token - The text token from OpenAI streaming
   * @param {number} [turnId] - Optional turn ID for ownership verification
   */
  onOpenAIDelta(token, turnId = null) {
    if (!this.realtimeTTS || !this.isActive) {
      console.warn("[StreamingTTS] Not active - ignoring delta");
      return;
    }

    // Optionally: throttle/log less if noisy
    // console.log('[StreamingTTS] Processing delta:', token);
    this.realtimeTTS.handleLLMDelta(token, turnId);
  }

  /**
   * End assistant turn - called when OpenAI streaming is complete
   */
  onAssistantTurnEnd() {
    if (!this.realtimeTTS || !this.isActive) {
      console.warn("[StreamingTTS] Not active - cannot end assistant turn");
      return;
    }

    console.log("[StreamingTTS] Ending assistant turn");
    this.realtimeTTS.flushAndClose(); // Persistent mode: flush only, keep WS open
    this.isActive = false;

    // Stop speaking animation after a delay to allow final audio
    setTimeout(() => {
      if (this.modelController?.stopSpeaking) {
        this.modelController.stopSpeaking();
      }

      // Notify callbacks
      if (this.onSpeechEnd) {
        this.onSpeechEnd();
      }
    }, 1000);
  }

  /**
   * Cancel current speech - called when user interrupts (barge-in)
   */
  onUserBargeIn() {
    if (!this.realtimeTTS) {
      console.warn("[StreamingTTS] Not initialized - cannot handle barge-in");
      return;
    }

    console.log("[StreamingTTS] User barge-in detected - canceling speech");
    this.realtimeTTS.cancel(); // Persistent mode: resets audio/text; keeps WS alive
    this.isActive = false;

    // Stop speaking animation immediately
    if (this.modelController?.stopSpeaking) {
      this.modelController.stopSpeaking();
    }

    // Notify callbacks
    if (this.onSpeechEnd) {
      this.onSpeechEnd();
    }
  }

  /**
   * Update character voice configuration
   * In persistent mode, we must reconnect to apply the new voice ID.
   */
  async updateCharacterVoice(characterId) {
    const voiceConfig = this.getCharacterVoiceConfig(characterId);
    if (!voiceConfig) {
      console.warn(
        `[StreamingTTS] No voice config for character: ${characterId}`,
      );
      return false;
    }

    this.currentCharacterVoiceId = voiceConfig.voice_id;

    if (this.realtimeTTS) {
      if (this.realtimeTTS.persistentWs) {
        // Reconnect so the new voice takes effect (voice is in WS URL)
        this.realtimeTTS.setVoiceId(this.currentCharacterVoiceId);
        this.realtimeTTS.shutdown(); // close old socket
        await this.realtimeTTS.start(); // open new socket with new voice
      } else {
        // Non-persistent: just set the voice; next start() will use it
        this.realtimeTTS.setVoiceId(this.currentCharacterVoiceId);
      }
    }

    console.log(
      `[StreamingTTS] Updated voice to: ${this.currentCharacterVoiceId}`,
    );
    return true;
  }

  /**
   * Check if streaming TTS is currently active
   */
  isStreamingActive() {
    return this.isActive;
  }

  /**
   * Get current status information
   */
  getStatus() {
    return {
      initialized: !!this.realtimeTTS,
      active: this.isActive,
      characterVoiceId: this.currentCharacterVoiceId,
      hasApiKey: !!this.elevenLabsApiKey,
    };
  }

  /**
   * Initialize lip sync with model controller blend shapes
   */
  _initializeLipSync() {
    if (!this.realtimeTTS) {
      console.warn(
        "[StreamingTTS] Cannot initialize lip sync - RealtimeTTS not available",
      );
      return;
    }

    // Check if we have access to model blend shapes through global window
    if (window.modelViewer && window.modelViewer.blendShapeControls) {
      console.log(
        "[StreamingTTS] Initializing lip sync with blend shape controls",
      );

      // Create LipSyncController with the blend shape controls
      const lipSyncController = new MinimalLipSyncController({
        blendShapeControls: window.modelViewer.blendShapeControls,
        debug: true,
      });

      // Enable lip sync on the realtime TTS
      this.realtimeTTS.enableLipSync(lipSyncController, {
        debug: true,
        frameMs: 33,
        minRms: 0.015,
        hissRatio: 1.35,
        intensityGain: 1.1,
        // enableMouthDrive: false // default is false in RealTimeLipSyncTap
      });

      console.log("[StreamingTTS] Lip sync initialized successfully");
    } else {
      console.warn(
        "[StreamingTTS] Cannot initialize lip sync - blend shape controls not available",
      );
      console.log("[StreamingTTS] Available on window:", {
        modelViewer: !!window.modelViewer,
        blendShapeControls: !!(
          window.modelViewer && window.modelViewer.blendShapeControls
        ),
      });
    }
  }

  /**
   * Graceful shutdown for app/page close
   */
  shutdown() {
    if (this.realtimeTTS) {
      this.realtimeTTS.shutdown();
    }
  }
}
